// Thin fetch wrapper. SPA + APIs share one CloudFront distribution in prod —
// same-origin = no CORS, no cookie domain headache. Defaults are the
// relative paths CloudFront routes to the Lambda Function URLs. In local
// dev we override with VITE_* pointing at the Hono dev servers.

const ADMIN_BASE = (import.meta.env.VITE_ADMIN_API_URL as string) ?? "/api/admin";
const PUBLIC_BASE = (import.meta.env.VITE_PUBLIC_API_URL as string) ?? "/api";

async function call<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type") && init?.body) headers.set("Content-Type", "application/json");
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = new Error((body && body.message) ?? `${res.status} ${res.statusText}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return body as T;
}

export interface Announcement {
  id: string;
  source_id: string | null;
  service_id: string;
  lang: string;
  title: string;
  body: string;
  image_url: string | null;
  tags: string[];
  starts_at: string;
  ends_at: string;
  created_at: string;
}

export interface PushMessage {
  id: string;
  service_id: string;
  lang: string | null;
  title: string;
  body: string;
  announcement_id: string | null;
  scheduled_at: string;
  sent_at: string | null;
  created_at: string;
}

export type InboundType = "feedback" | "inquiry";
export type InboundStatus = "open" | "resolved";

export interface Inbound {
  id: string;
  service_id: string;
  type: InboundType;
  user_ref: string | null;
  location: string | null;
  context: unknown;
  body: string | null;
  contact: string | null;
  app_version: string | null;
  status: InboundStatus;
  resolution: string | null;
  received_at: string;
  resolved_at: string | null;
}

export const api = {
  // ---- auth -------------------------------------------------------------
  requestCode(email: string) {
    return call<void>(ADMIN_BASE, "/auth/request-code", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
  verify(email: string, code: string) {
    return call<{ email: string }>(ADMIN_BASE, "/auth/verify", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    });
  },
  me() {
    return call<{ email: string }>(ADMIN_BASE, "/auth/me");
  },
  logout() {
    return call<void>(ADMIN_BASE, "/auth/logout", { method: "POST" });
  },

  // ---- announcements ----------------------------------------------------
  listAnnouncements(params: { service: string; lang?: string; tag?: string; limit?: number; include_hidden?: boolean }) {
    const q = new URLSearchParams();
    q.set("service", params.service);
    if (params.lang) q.set("lang", params.lang);
    if (params.tag) q.set("tag", params.tag);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.include_hidden) q.set("include_hidden", "1");
    return call<{ items: Announcement[] }>(PUBLIC_BASE, `/announcements?${q.toString()}`);
  },
  getAnnouncement(id: string) {
    return call<{ item: Announcement; translations: Announcement[] }>(PUBLIC_BASE, `/announcements/${id}`);
  },
  createAnnouncement(body: Record<string, unknown>) {
    return call<{ original: Announcement; translations: Announcement[] }>(
      ADMIN_BASE,
      "/announcements",
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  updateAnnouncement(id: string, body: Record<string, unknown>) {
    return call<Announcement>(ADMIN_BASE, `/announcements/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  translateAnnouncement(id: string, body: { target_langs: string[]; overwrite?: boolean }) {
    return call<{ original: Announcement; translations: Announcement[] }>(
      ADMIN_BASE,
      `/announcements/${id}/translate`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  presignImage(body: { filename: string; content_type: string }) {
    return call<{ upload_url: string; public_url: string; key: string }>(
      ADMIN_BASE,
      "/announcements/images",
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  // ---- push -------------------------------------------------------------
  listPushMessages(params: { service?: string; status?: "pending" | "sent" | "all"; limit?: number }) {
    const q = new URLSearchParams();
    if (params.service) q.set("service", params.service);
    if (params.status) q.set("status", params.status);
    if (params.limit) q.set("limit", String(params.limit));
    return call<{ items: PushMessage[]; total: number }>(ADMIN_BASE, `/push-messages?${q.toString()}`);
  },
  createPushMessage(body: Record<string, unknown>) {
    return call<PushMessage>(ADMIN_BASE, "/push-messages", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  updatePushMessage(id: string, body: Record<string, unknown>) {
    return call<PushMessage>(ADMIN_BASE, `/push-messages/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  deletePushMessage(id: string) {
    return call<void>(ADMIN_BASE, `/push-messages/${id}`, { method: "DELETE" });
  },

  // ---- inbound ----------------------------------------------------------
  listInbound(params: {
    service?: string;
    type?: InboundType;
    status?: InboundStatus;
    since?: string;
    limit?: number;
  }) {
    const q = new URLSearchParams();
    if (params.service) q.set("service", params.service);
    if (params.type) q.set("type", params.type);
    if (params.status) q.set("status", params.status);
    if (params.since) q.set("since", params.since);
    if (params.limit) q.set("limit", String(params.limit));
    return call<{ items: Inbound[]; total: number }>(ADMIN_BASE, `/inbound?${q.toString()}`);
  },
  getInbound(id: string) {
    return call<Inbound>(ADMIN_BASE, `/inbound/${id}`);
  },
  updateInbound(id: string, body: Record<string, unknown>) {
    return call<Inbound>(ADMIN_BASE, `/inbound/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
};
